import React, { useState, useRef, useEffect } from 'react';
import { MOCK_USERS_BY_ROLE } from '../SimulatedUserCardSelector';
import { useAuthStore } from '../../../../store/authStore';

interface UserProfileProps {
  activeRole: string;
  theme: 'dark' | 'light' | 'purple';
  onStartMenuToggle: () => void;
  onRoleSelect: (roleKey: string) => void;
  onActorSelect?: (roleKey: string, actorId: string, actorName: string) => void;
  rolePrimary?: string;
  roleSecondary?: string;
  onOpenProfile?: () => void;
  onOpenSettings?: () => void;
}

// Hierarquia de visibilidade: indice menor = mais alto na hierarquia
export const ROLES_LIST = [
  {
    id: 'administrador',
    name: 'Administrador',
    hierarchyLevel: 0,
    primaryRgb: '155, 108, 255',
    secondaryRgb: '61, 229, 255',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3 4 6v5c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6z" />
        <circle cx="12" cy="11" r="2.5" />
        <path d="M8.5 17a3.5 3.5 0 0 1 7 0" />
      </svg>
    )
  },
  {
    id: 'prefeito',
    name: 'Prefeito',
    hierarchyLevel: 1,
    primaryRgb: '216, 173, 84',
    secondaryRgb: '143, 92, 255',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18" />
        <path d="M5 21V10h14v11" />
        <path d="m3 10 9-6 9 6" />
        <path d="M9 21v-7h6v7" />
        <path d="M8 10v7M16 10v7" />
      </svg>
    )
  },
  {
    id: 'diretor',
    name: 'Diretor',
    hierarchyLevel: 2,
    primaryRgb: '89, 113, 255',
    secondaryRgb: '61, 218, 255',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3 4 6v5c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6z" />
        <path d="m7 11 5-3 5 3-5 3z" />
        <path d="M9 13v3c2 1.5 4 1.5 6 0v-3" />
      </svg>
    )
  },
  {
    id: 'coordenador',
    name: 'Coordenador',
    hierarchyLevel: 3,
    primaryRgb: '232, 121, 249',
    secondaryRgb: '249, 168, 212',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="12" r="2.5" />
        <circle cx="18" cy="6" r="2.5" />
        <circle cx="18" cy="18" r="2.5" />
        <path d="m8.4 10.8 7.2-3.6M8.4 13.2l7.2 3.6" />
        <path d="M12 5v14" />
      </svg>
    )
  },
  {
    id: 'secretaria',
    name: 'Secretaria',
    hierarchyLevel: 3,
    primaryRgb: '255, 119, 133',
    secondaryRgb: '255, 191, 91',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2h8l4 4v16H6z" />
        <path d="M14 2v5h5" />
        <path d="M9 11h6M9 15h6" />
        <circle cx="15.5" cy="18.5" r="2.5" />
        <path d="m14.4 18.5.8.8 1.5-1.6" />
      </svg>
    )
  },
  {
    id: 'professor',
    name: 'Professor',
    hierarchyLevel: 4,
    primaryRgb: '88, 225, 157',
    secondaryRgb: '73, 195, 255',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="m2 9 10-5 10 5-10 5z" />
        <path d="M6 11v5c4 3 8 3 12 0v-5" />
        <path d="M20 9v6" />
        <path d="M4 20h7" />
        <path d="m7 17 3 3-3 3" />
      </svg>
    )
  },
  {
    id: 'responsavel',
    name: 'Responsavel',
    hierarchyLevel: 5,
    primaryRgb: '255, 102, 169',
    secondaryRgb: '255, 171, 122',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 21V9l8-6 8 6v12" />
        <path d="M8 21v-7h8v7" />
        <path d="M12 12s-3-1.7-3-4a2 2 0 0 1 3-1.4A2 2 0 0 1 15 8c0 2.3-3 4-3 4z" />
      </svg>
    )
  },
  {
    id: 'aluno',
    name: 'Aluno',
    hierarchyLevel: 6,
    primaryRgb: '255, 140, 66',
    secondaryRgb: '255, 107, 157',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="2.5" width="14" height="19" rx="2" />
        <path d="M8 7h8M9 18h6" />
        <path d="m8 11 4-2 4 2-4 2z" />
      </svg>
    )
  }
];

// Mapeia perfilTipo do backend para o nivel hierarquico minimo visivel
function getMinVisibleLevel(perfilTipo: string): number {
  switch (perfilTipo) {
    case 'ADMIN': return 0;
    case 'REDE':
    case 'ESTADO': return 1;
    case 'MUNICIPIO': return 2;
    case 'ESCOLA_DIRETOR': return 3;
    case 'ESCOLA_COORDENACAO': return 4;
    case 'ESCOLA_SECRETARIA': return 5;
    default: return 6;
  }
}

export const UserProfile: React.FC<UserProfileProps> = ({
  activeRole,
  theme,
  onStartMenuToggle,
  onRoleSelect,
  onActorSelect,
  rolePrimary,
  roleSecondary,
  onOpenProfile,
  onOpenSettings,
}) => {
  const [hoveredRole, setHoveredRole] = useState<string | null>(null);
  const [availableActors, setAvailableActors] = useState<any[]>([]);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ESTADO DO DROPDOWN DO PERFIL ATIVO
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Determina perfis disponíveis baseando-se na hierarquia do usuário autenticado
  const visibleRoles = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('ac_user') || 'null');
      if (!user) return [];
      const perfil = (user.perfil || '').toUpperCase();
      const cargo = (user.cargo || '').toUpperCase();

      if (perfil === 'ADMIN') {
        return ROLES_LIST;
      }
      // Senha pública do /simulador (SIM_STUDIO) = entrada como Prefeito
      if (perfil === 'SIM_STUDIO' || perfil === 'MUNICIPAL' || perfil === 'MUNICIPIO') {
        return ROLES_LIST.filter(r => r.id !== 'administrador');
      }
      if (perfil === 'ESCOLA') {
        if (cargo === 'DIRETOR') {
          return ROLES_LIST.filter(r => r.id !== 'administrador' && r.id !== 'prefeito');
        }
        if (cargo === 'COORDENACAO' || cargo === 'COORDENADOR') {
          return ROLES_LIST.filter(r => r.id === 'coordenador');
        }
        if (cargo === 'SECRETARIA') {
          return ROLES_LIST.filter(r => r.id === 'secretaria');
        }
        return ROLES_LIST.filter(r => r.id !== 'administrador' && r.id !== 'prefeito');
      }
      if (perfil === 'PROFESSOR') {
        return ROLES_LIST.filter(r => r.id === 'professor');
      }
      if (perfil === 'RESPONSAVEL') {
        return ROLES_LIST.filter(r => r.id === 'responsavel');
      }
      if (perfil === 'ALUNO') {
        return ROLES_LIST.filter(r => r.id === 'aluno');
      }
      return [];
    } catch {
      return [];
    }
  })();

  // Busca atores disponiveis para o role ao hover (usa MOCK_USERS_BY_ROLE)
  const loadActorsForRole = (roleId: string) => {
    const mockUsers = MOCK_USERS_BY_ROLE[roleId.toLowerCase()] || [];
    // Transforma mock para formato compativel com o tooltip
    const actors = mockUsers.map(u => ({
      user: { id: u.id, nome: u.name, perfil: u.role }
    }));
    setAvailableActors(actors);
  };

  const handleMouseEnter = (roleId: string) => {
    if (roleId === 'administrador') return;
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      loadActorsForRole(roleId);
      setHoveredRole(roleId);
    }, 300);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredRole(null);
      setAvailableActors([]);
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const activeRoleData = ROLES_LIST.find(r => r.id === activeRole.toLowerCase()) || ROLES_LIST[0];

  const mockUsersForActiveRole = MOCK_USERS_BY_ROLE[activeRole.toLowerCase()] || [];

  const activeActor = (() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`ac_selected_actor_${activeRole.toLowerCase()}`) || 'null');
      if (!saved) return null;
      return mockUsersForActiveRole.find((u: any) => u.id === saved.userId) || {
        id: saved.userId,
        name: saved.userName || 'Usuário Simulado',
        role: activeRoleData.name,
        email: `${saved.userName?.toLowerCase().replace(/\s+/g, '') || 'usuario'}@alunoconectado.com.br`,
        avatar: saved.userName?.substring(0, 2).toUpperCase() || 'US',
        color: `rgb(${activeRoleData.primaryRgb})`,
      };
    } catch {
      return null;
    }
  })();

  const activeUser = activeActor || mockUsersForActiveRole[0];

  return (
    <div className="flex items-center gap-3 select-none flex-shrink-0">
      {/* Pílula do Perfil Ativo com seu Dropdown de Contexto */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(prev => !prev)}
          className="h-10 px-5 rounded-2xl flex items-center gap-2.5 relative bg-white/5 border border-white/10 shadow-lg text-[11px] font-bold text-white tracking-wide cursor-pointer active:scale-95 hover:bg-white/10 transition-all"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.01) 100%), rgba(20,20,35,0.4)',
          }}
        >
          <span className="w-4 h-4 flex items-center justify-center text-white [&_svg]:w-4 [&_svg]:h-4">
            {activeRoleData.icon}
          </span>
          <span>{activeRoleData.name}</span>
          
          {/* Glow Active Underline */}
          <span 
            className="absolute bottom-[3px] left-[15%] right-[15%] h-[2.5px] rounded-full"
            style={{
              background: `linear-gradient(90deg, rgb(${activeRoleData.secondaryRgb}), rgb(${activeRoleData.primaryRgb}))`,
              boxShadow: `0 0 10px rgba(${activeRoleData.primaryRgb}, 0.8)`
            }}
          />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && activeUser && (
          <div 
            className="absolute bottom-12 left-0 w-64 rounded-2xl border p-4 z-[10000] backdrop-blur-[30px] shadow-2xl flex flex-col gap-4 text-white animate-slide-up"
            style={{
              background: 'rgba(18, 15, 38, 0.95)',
              borderColor: `rgba(${activeRoleData.primaryRgb}, 0.3)`,
              boxShadow: `0 15px 40px rgba(0,0,0,0.5), 0 0 20px rgba(${activeRoleData.primaryRgb}, 0.1)`,
            }}
          >
            {/* Dropdown Header */}
            <div className="flex items-center gap-3 pb-3 border-b border-white/10">
              {/* Circle Avatar */}
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-[12px] font-black text-white border border-white/20 shadow-md uppercase"
                style={{
                  background: `linear-gradient(135deg, rgb(${activeRoleData.primaryRgb}) 0%, rgb(${activeRoleData.secondaryRgb}) 100%)`,
                }}
              >
                {activeUser.avatar || activeUser.name.substring(0, 2).toUpperCase()}
              </div>
              
              {/* User Info */}
              <div className="flex flex-col text-left min-w-0">
                <strong className="text-[12px] font-extrabold text-white truncate" title={activeUser.name}>
                  {activeUser.name}
                </strong>
                <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                  <span 
                    className="px-1.5 py-0.5 rounded text-[7px] font-black uppercase tracking-wider flex-shrink-0"
                    style={{
                      background: `rgba(${activeRoleData.primaryRgb}, 0.2)`,
                      color: `rgb(${activeRoleData.primaryRgb})`,
                      border: `1px solid rgba(${activeRoleData.primaryRgb}, 0.3)`
                    }}
                  >
                    {activeUser.role || activeRoleData.name}
                  </span>
                  <span className="text-[8.5px] text-[#9aa3c0] truncate" title={activeUser.email}>
                    {activeUser.email || 'sem-email@teste.com'}
                  </span>
                </div>
              </div>
            </div>

            {/* Dropdown Body */}
            <div className="flex flex-col gap-1">
              <button 
                onClick={() => {
                  if (activeRole.toLowerCase() === 'administrador') {
                    if (window.confirm("Deseja ir para a página de perfil administrativo (/admin/master/)?")) {
                      window.location.href = '/admin/master/';
                    }
                  } else {
                    if (window.confirm("Deseja abrir a página de Perfil?")) {
                      onOpenProfile?.();
                    }
                  }
                  setIsDropdownOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-transparent border-none text-left text-[10px] text-[#cdd6f4] hover:bg-white/5 cursor-pointer transition-all"
              >
                <span>👤</span>
                <span>Meu Perfil</span>
              </button>
              <button 
                onClick={() => {
                  if (activeRole.toLowerCase() === 'administrador') {
                    if (window.confirm("Deseja ir para a página de configurações (/master/configuracoes)?")) {
                      window.location.href = '/master/configuracoes';
                    }
                  } else {
                    if (window.confirm("Deseja abrir a página de Configurações?")) {
                      onOpenSettings?.();
                    }
                  }
                  setIsDropdownOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-transparent border-none text-left text-[10px] text-[#cdd6f4] hover:bg-white/5 cursor-pointer transition-all"
              >
                <span>⚙️</span>
                <span>Configurações</span>
              </button>
            </div>

            {/* Dropdown Body: Perfis Disponíveis (Alternar Perfil) */}
            {visibleRoles.length > 1 && (
              <div className="flex flex-col gap-1 border-t border-white/10 pt-2.5">
                <span className="text-[8px] font-extrabold text-white/40 uppercase tracking-widest px-3 mb-1.5 block text-left">
                  Alternar Perfil
                </span>
                <div className="grid grid-cols-2 gap-1.5 px-1">
                  {visibleRoles.map(r => {
                    const isActive = r.id === activeRole.toLowerCase();
                    return (
                      <button
                        key={r.id}
                        onClick={() => {
                          if (!isActive) {
                            onRoleSelect(r.id);
                          }
                          setIsDropdownOpen(false);
                        }}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[9px] font-bold border transition-all text-left truncate cursor-pointer ${
                          isActive
                            ? 'bg-white/10 border-white/20 text-[#a78bfa]'
                            : 'bg-transparent border-transparent text-[#a6adc8] hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center [&_svg]:w-3.5 [&_svg]:h-3.5 text-white">
                          {r.icon}
                        </span>
                        <span className="truncate">{r.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Dropdown Footer (Sair) */}
            <div className="border-t border-white/10 pt-2 flex flex-col gap-1.5">
              {activeRole !== 'administrador' && visibleRoles.some(r => r.id === 'administrador') && (
                <button 
                  onClick={() => {
                    setIsDropdownOpen(false);
                    if (window.confirm("Deseja parar de simular este perfil e retornar ao painel Administrador?")) {
                      onRoleSelect('administrador');
                    }
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-left text-[10px] text-white hover:text-white cursor-pointer transition-all font-semibold"
                >
                  <span>🔄</span>
                  <span>Voltar para Administrador</span>
                </button>
              )}

              <button 
                onClick={async () => {
                  setIsDropdownOpen(false);
                  if (window.confirm("Deseja encerrar totalmente a sua sessão e sair do Aluno Conectado?")) {
                    await useAuthStore.getState().logout();
                    window.location.href = '/login';
                  }
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-[#ff7785]/15 hover:bg-[#ff7785]/25 border border-[#ff7785]/20 text-left text-[10px] text-[#ff7785] cursor-pointer transition-all font-bold"
              >
                <span>🚪</span>
                <span>Encerrar Sessão (Logout)</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Botao Iniciar - Ocultado conforme solicitado */}

      {/* Role Selector Strip — toolbar horizontal só com ícones */}
      {visibleRoles.length > 1 && (
        <nav
          className="flex items-center gap-1 p-1 bg-black/25 border border-white/5 rounded-2xl relative"
          aria-label="Troca rápida de ambiente"
        >
          {visibleRoles.map((role) => {
            const isActive = activeRole.toLowerCase() === role.id;
            const isHovered = hoveredRole === role.id;
            const savedActor = (() => {
              try {
                return JSON.parse(localStorage.getItem(`ac_selected_actor_${role.id}`) || 'null');
              } catch {
                return null;
              }
            })();

            return (
              <div
                key={role.id}
                className="relative"
                onMouseEnter={() => handleMouseEnter(role.id)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  type="button"
                  onClick={() => onRoleSelect(role.id)}
                  className={`w-9 h-9 rounded-xl flex items-center justify-center relative cursor-pointer transition-all active:scale-90 hover:scale-105 group border-none ${
                    isActive
                      ? 'text-white'
                      : 'text-[#9aa3c0] hover:text-white hover:bg-white/5'
                  }`}
                  style={{
                    background: isActive
                      ? `linear-gradient(145deg, rgba(${role.primaryRgb}, 0.27), rgba(${role.secondaryRgb}, 0.14))`
                      : 'transparent',
                    boxShadow: isActive
                      ? `inset 0 0 0 1px rgba(255, 255, 255, 0.09), 0 6px 16px rgba(${role.primaryRgb}, 0.14)`
                      : 'none',
                  }}
                  title={`${role.name}${savedActor ? ` — ${savedActor.userName}` : ''}`}
                >
                  <span className="relative z-10 transition-transform group-hover:scale-110 [&_svg]:w-5 [&_svg]:h-5">
                    {role.icon}
                  </span>
                  {isActive && (
                    <span
                      className="absolute bottom-[3px] left-[25%] right-[25%] h-[2.5px] rounded-full"
                      style={{
                        background: `linear-gradient(90deg, rgb(${role.secondaryRgb}), rgb(${role.primaryRgb}))`,
                        boxShadow: `0 0 8px rgb(${role.primaryRgb})`,
                      }}
                    />
                  )}
                </button>

                {isHovered && role.id !== 'administrador' && (
                  <div
                    onMouseEnter={() => {
                      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
                    }}
                    onMouseLeave={handleMouseLeave}
                    className="absolute bottom-[48px] left-1/2 -translate-x-1/2 min-w-[180px] max-w-[240px] rounded-xl border border-white/10 bg-[#1e1e2e]/95 backdrop-blur-xl shadow-2xl z-[99999] p-2"
                    style={{
                      boxShadow: `0 16px 48px rgba(0,0,0,0.5), 0 0 20px rgba(${role.primaryRgb}, 0.15)`,
                    }}
                  >
                    <div className="text-[9px] font-bold uppercase tracking-wider text-[#a6adc8] px-2 pb-1.5 border-b border-white/5 mb-1.5">
                      {role.name} — Usuários
                    </div>
                    {availableActors.length === 0 ? (
                      <div className="text-[10px] text-[#585b70] px-2 py-2 text-center">
                        Nenhum ator cadastrado
                      </div>
                    ) : (
                      <div className="flex flex-col gap-0.5 max-h-[200px] overflow-y-auto">
                        {availableActors.map((actor: any) => {
                          const isSaved = savedActor?.userId === actor.user.id;
                          return (
                            <button
                              key={actor.user.id}
                              type="button"
                              onClick={() => {
                                localStorage.setItem(
                                  `ac_selected_actor_${role.id}`,
                                  JSON.stringify({
                                    userId: actor.user.id,
                                    userName: actor.user.nome,
                                  }),
                                );
                                setHoveredRole(null);
                                if (onActorSelect) {
                                  onActorSelect(role.id, actor.user.id, actor.user.nome);
                                }
                              }}
                              className={`w-full text-left px-2 py-1.5 rounded-lg text-[10px] font-medium transition-all border-none cursor-pointer flex items-center gap-2 ${
                                isSaved
                                  ? 'bg-white/10 text-white'
                                  : 'bg-transparent text-[#cdd6f4] hover:bg-white/5'
                              }`}
                            >
                              <span
                                className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold"
                                style={{
                                  background: `rgba(${role.primaryRgb}, 0.25)`,
                                  color: `rgb(${role.primaryRgb})`,
                                }}
                              >
                                {actor.user.nome?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                              <span className="flex-1 truncate">{actor.user.nome}</span>
                              {isSaved && <span className="text-[8px]">✓</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="absolute -bottom-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 bg-[#1e1e2e]/95 border-r border-b border-white/10" />
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      )}
    </div>
  );
};
